import React from 'react'
import { Box, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '32px',
})

const StatusItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#5d6870',
})

const StatusCount = styled(Box)({
    fontWeight: 'bold',
})

const StatusLabel = styled(Box)({
    color: '#5d6870',
})

// Circular progress indicator (no icon inside)
const CircularProgressRing = ({
    percentage,
    color,
    bgColor,
    size = 24,
    strokeWidth = 3,
}) => {
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (percentage / 100) * circumference

    return (
        <svg
            width={size}
            height={size}
            style={{
                transform: 'rotate(-90deg)',
            }}
        >
            {/* Background circle */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={bgColor || '#e0e0e0'}
                strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            {percentage > 0 && (
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                />
            )}
        </svg>
    )
}

const RowStatus = ({ item }) => {
    const { t } = useTranslation()

    // Extract counts from item object
    const pendingCount =
        item?.totalItemValidationPending || item?.totalItemProcessPending || 10
    const validCount =
        item?.totalItemValidationSucceded ||
        item?.totalItemProcessSucceded ||
        10
    const errorCount =
        item?.totalItemValidationFailed ||
        item?.totalItemProcessFailed ||
        item?.failedCount ||
        10
    const totalCount =
        item?.totalItemCount || pendingCount + validCount + errorCount || 0

    // Calculate percentage for pending (partial fill)
    const pendingPercentage =
        totalCount > 0 ? (pendingCount / totalCount) * 100 : 0

    // Build status items array - only show if count > 0
    const statusItems = []

    if (pendingCount > 0) {
        statusItems.push({
            key: 'pending',
            count: pendingCount,
            label: t('Pending'),
            color: '#e67e22', // Orange
            bgColor: '#e0e0e0',
            percentage: pendingPercentage,
            icon: 'PendingStatus',
        })
    }

    if (validCount > 0) {
        statusItems.push({
            key: 'valid',
            count: validCount,
            label: t('Valid'),
            color: '#27ae60', // Green
            bgColor: '#27ae60',
            percentage: 100, // Full circle for valid
            icon: 'Check',
        })
    }

    if (errorCount > 0) {
        statusItems.push({
            key: 'error',
            count: errorCount,
            label: t('Errors'),
            color: '#e74c3c', // Red
            bgColor: '#e74c3c',
            percentage: 100, // Full circle for errors
            icon: 'ErrorStatus',
        })
    }

    if (statusItems.length === 0) {
        return null
    }

    return (
        <Container>
            {statusItems.map((status) => (
                <StatusItem key={status.key}>
                    <CircularProgressRing
                        percentage={status.percentage}
                        color={status.color}
                        bgColor={status.bgColor}
                    />
                    <Icon
                        name={status.icon}
                        width="14px"
                        height="14px"
                        color={'#5d6870'}
                    />
                    <StatusCount>{status.count}</StatusCount>
                    <StatusLabel>{status.label}</StatusLabel>
                </StatusItem>
            ))}
        </Container>
    )
}

export default RowStatus
